import React, { useEffect, useState, useRef } from "react";
import "@xyflow/react/dist/style.css";

export const DependencyLines = ({ tasks, taskDates, viewType, getHeight }) => {
  const [edges, setEdges] = useState([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const previousViewTypeRef = useRef(viewType);

  const DEP = {
    FinishToStart: 0,
    StartToStart: 1,
  };

  const anchor = (rect, side, container) => {
    const x =
      side === "start" ? rect.left - container.left : rect.right - container.left;
    const y = rect.top + rect.height / 2 - container.top;
    return { x, y };
  };

  const makeSmoothPath = (sx, sy, tx, ty) => {
    const dx = tx - sx;
    const dy = ty - sy;
    
    const cx1 = sx + dx / 3;
    const cy1 = sy;
    const cx2 = sx + (2 * dx) / 3;
    const cy2 = ty;
    return `M ${sx} ${sy} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${tx} ${ty}`;
  };

  const updateEdgePositions = () => {
    const nextEdges = [];
    const timelineContainer = document.querySelector(".timeline-container");
    if (!timelineContainer) {
      setEdges(nextEdges);
      return;
    }
  
    const containerBounds = timelineContainer.getBoundingClientRect();
    const scrollTop = timelineContainer.scrollTop || 0;
  
    tasks.forEach((successor) => {
      (successor.dependencies || []).forEach((dep) => {
        const predecessor = tasks.find((t) => t.id === dep.task?.id);
        if (!predecessor) return;
  
        const predEl = document.getElementById(`task-bar-${predecessor.id}`);
        const succEl = document.getElementById(`task-bar-${successor.id}`);
        if (!predEl || !succEl) return;
  
        const predRect = predEl.getBoundingClientRect();
        const succRect = succEl.getBoundingClientRect();
  
        let source, target, pathString, isShowArrow = false;
        switch (dep.dependencyType) {
          case DEP.StartToStart: {
            source = {
              x: succRect.left - containerBounds.left,
              y: succRect.top + succRect.height / 2 - containerBounds.top,
            };
            target = {
              x: predRect.left - containerBounds.left,
              y: predRect.top + predRect.height / 2 - containerBounds.top,
            };
  
            // Apply scroll offset
            source.y += scrollTop;
            target.y += scrollTop;
  
            // Loop outward to the left
            const offset = 5; // how far left the loop goes
            const midX = Math.min(source.x, target.x) - offset;
  
            isShowArrow = Math.sqrt(Math.pow(target.x - source.x, 2) + Math.pow(target.y - source.y, 2)) >= 50 ;
            pathString = `
              M ${source.x} ${source.y}
              C ${midX} ${source.y},
                ${midX} ${target.y},
                ${isShowArrow ? target.x -3 : target.x} ${target.y}
            `;
            break;
          }
  
          case DEP.FinishToStart: {
            source = {
              x: predRect.right - containerBounds.left,
              y: predRect.top + predRect.height / 2 - containerBounds.top,
            };
            target = {
              x: succRect.left - containerBounds.left,
              y: succRect.top + succRect.height / 2 - containerBounds.top,
            };
  
            // Apply scroll offset
            source.y += scrollTop;
            target.y += scrollTop;

            isShowArrow = Math.sqrt(Math.pow(target.x - source.x, 2) + Math.pow(target.y - source.y, 2)) >= 50 ;

            pathString = makeSmoothPath(source.x, source.y, isShowArrow ? target.x -3 : target.x, target.y);
            break;
          }
  
          default:
            return;
        }
  
        nextEdges.push({
          id: `${predecessor.id}->${successor.id}`,
          pathString,
          style: { stroke: "#342C82", strokeWidth: 1.5 },
          dependencyType: dep.dependencyType,
          showArrow: isShowArrow,
        });
      });
    });
  
    setEdges(nextEdges);
  };
  

  useEffect(() => {
    if (previousViewTypeRef.current !== viewType) {
      setIsTransitioning(true);
      requestAnimationFrame(() => {
        updateEdgePositions();
        setTimeout(() => setIsTransitioning(false), 300);
      });
      previousViewTypeRef.current = viewType;
    }
  }, [viewType]);

  useEffect(() => {
    if (!isTransitioning) {
      const timer = setTimeout(() => {
        updateEdgePositions();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [tasks, taskDates, isTransitioning]);

  useEffect(() => {
    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (isTransitioning) {
          requestAnimationFrame(updateEdgePositions);
        }
      }, 16);
    };

    const timelineContainer = document.querySelector(".timeline-container");
    if (timelineContainer) {
      timelineContainer.addEventListener("scroll", handleScroll, {
        passive: true,
      });
    }

    return () => {
      if (timelineContainer) {
        timelineContainer.removeEventListener("scroll", handleScroll);
      }
      clearTimeout(scrollTimeout);
    };
  }, [isTransitioning]);

  return (
    <div className="absolute inset-0 pointer-events-none z-0">
      <svg
        className="w-full"
        style={{
          height: `${getHeight()}px`,
        }}
      >
        <defs>
          <marker
            id="arrowhead-forward"
            markerWidth="6"
            markerHeight="4"
            refX="3"
            refY="2"
            orient="auto"
            markerUnits="userSpaceOnUse"
          >
            <path 
              d="M 0 0 L 4 2 L 0 4"
              fill="none"
              stroke="#342C82"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </marker>
        </defs>

        {edges.map((edge) => (
          <path
            key={edge.id}
            d={edge.pathString}
            fill="none"
            stroke={edge.style.stroke}
            strokeWidth={edge.style.strokeWidth}
            markerEnd={edge.showArrow ? "url(#arrowhead-forward)" : "none"}
            className={
              isTransitioning ? "transition-all duration-300 ease-in-out" : ""
            }
            style={{
              filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.1))"
            }}
          />
        ))}
      </svg>
    </div>
  );
};